import { CanvasTagId, CanvasTemplateMinimal, CanvasTemplateTag } from "shared/datamodel/schemas/canvas-template";
import type { CoverType, TemplatesSection } from "../../../templates-modal";
import consts from "shared/consts";
import style from "./template-gallery-v2.module.css";
import { SearchField } from "frontend/ui-components/search-field/search-field";
import TemplateCardV2 from "../template-card/template-card-v2";
import { CSSProperties, useEffect, useRef } from "react";
import cn from "classnames";
import { CloseButton } from "frontend/ui-components/dialog-close-button/dialog-close-button";
import { Crown } from "frontend/icons/crown";
import TemplatesModalCovers from "../../shared/templates-modal-covers";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { cleanAccountName } from "shared/util/utils";
import { MondayLogo } from "frontend/icons/monday-logo";
import Skeleton from "react-loading-skeleton";

function setScrollableFadeEffect(element: HTMLElement) {
  const isScrollable = element.scrollHeight > element.clientHeight;
  // GUARD: If element is not scrollable, remove all classes
  if (!isScrollable) {
    element.classList.remove(style["is-bottom-overflowing"], style["is-top-overflowing"]);
    return;
  }

  // Otherwise, the element is overflowing!
  // Now we just need to find out which direction it is overflowing to (can be both).
  // One pixel is added to the height to account for non-integer heights.
  const isScrolledToBottom = element.scrollHeight <= element.clientHeight + element.scrollTop + 1;
  const isScrolledToTop = isScrolledToBottom ? false : element.scrollTop === 0;
  element.classList.toggle(style["is-bottom-overflowing"], !isScrolledToBottom);
  element.classList.toggle(style["is-top-overflowing"], !isScrolledToTop);
}

function ScrollWithFade({
  children,
  extraStyle,
  resetScrollKey,
}: React.PropsWithChildren<{
  extraStyle?: React.CSSProperties;
  resetScrollKey?: string | number;
}>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Reset scroll position when resetScrollKey changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [resetScrollKey]);

  return (
    <div
      ref={scrollContainerRef}
      onScroll={(e) => {
        const element = e.currentTarget;
        setScrollableFadeEffect(element);
      }}
      className={style.scrollableContent}
      style={extraStyle}
    >
      {children}
    </div>
  );
}

interface TemplateGalleryProps {
  sections: TemplatesSection[];
  tags: CanvasTemplateTag[];
  searchTerm: string;
  selectedTag: { id: CanvasTagId; name: string };
  coverType: CoverType | null;
  onSearch: (searchTerm: string) => void;
  onTagClicked: (tagId: CanvasTagId, tagName: string) => void;
  onSeeAllClick: (tagId: number, tagName: string) => void;
  onTemplateClick: (template: CanvasTemplateMinimal) => void;
  onPreviewTemplate: (template: CanvasTemplateMinimal) => void;
  onEditTemplate: (template: CanvasTemplateMinimal) => void;
  onDeleteTemplate: (templateId: string) => void;
  onDismiss: () => void;
  showUpgradeCTA?: boolean;
  onUpgradeCTAClick: (source: string) => void;
  onCreateTemplate?: () => void;
  syncedTemplatesSectionId: number;
  shouldShowSyncedOnTop: boolean;
  accountName: string;
}
function onscroll(e: Event) {
  const element = e.currentTarget as HTMLElement;
  element && setScrollableFadeEffect(element);
}
export function TemplateGallery({
  sections,
  tags,
  searchTerm,
  selectedTag,
  coverType,
  onSearch,
  onTagClicked,
  onSeeAllClick,
  onTemplateClick,
  onPreviewTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onDismiss,
  showUpgradeCTA,
  onUpgradeCTAClick,
  onCreateTemplate,
  syncedTemplatesSectionId,
  shouldShowSyncedOnTop,
  accountName,
}: TemplateGalleryProps) {
  // Helper function to get category item style with selection state
  const getCategoryItemStyle = (tagId?: CanvasTagId, extra?: string) =>
    cn(style.categoryItem, selectedTag.id === tagId && style.selected, extra);

  const scrollableContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = scrollableContentRef.current;
    if (element) {
      setScrollableFadeEffect(element);
      element.addEventListener("scroll", onscroll);
      return () => element.removeEventListener("scroll", onscroll);
    }
  }, []);

  const syncedTag = tags.find((tag) => tag.id === syncedTemplatesSectionId);

  return (
    <div className={style.container}>
      {/* Sidebar */}
      <div className={style.sidebar}>
        <form className="u-px-2 u-my-2" onSubmit={(e) => e.preventDefault()}>
          <SearchField
            name="template-search"
            searchValue={searchTerm}
            setSearchValue={onSearch}
            placeholder="Search Templates"
          />
        </form>

        <SimpleBar
          forceVisible={"y"}
          scrollbarMaxSize={150}
          style={{
            flex: "1 1 auto",
            minHeight: 0,
          }}
          scrollableNodeProps={{ className: style.scrollableContent, ref: scrollableContentRef }}
        >
          {/* Top part - fixed categories */}
          {showUpgradeCTA && (
            <div
              className={getCategoryItemStyle(undefined, style.unlockButton)}
              onClick={() => onUpgradeCTAClick("Unlock all templates")}
            >
              <Crown size={14} />
              <span>Unlock all templates</span>
            </div>
          )}

          {/* all templates */}
          <div
            className={getCategoryItemStyle(consts.ALL_TEMPLATES_TAG_ID)}
            onClick={() => onTagClicked(consts.ALL_TEMPLATES_TAG_ID, "All Templates")}
          >
            All Templates
          </div>

          {/*my template*/}
          <div
            className={getCategoryItemStyle(consts.MY_TEMPLATES_TAG_ID)}
            onClick={() => onTagClicked(consts.MY_TEMPLATES_TAG_ID, "My Templates")}
          >
            My Templates
          </div>
          {/* account templates*/}
          <div
            className={getCategoryItemStyle(consts.ACCOUNT_TEMPLATES_TAG_ID)}
            onClick={() => onTagClicked(consts.ACCOUNT_TEMPLATES_TAG_ID, `${accountName} Templates`)}
            data-testid="account-templates-tag"
          >
            {`${cleanAccountName(accountName)} Templates`}
          </div>

          {/* monday (synced) templates */}
          {shouldShowSyncedOnTop && syncedTag && (
            <div
              className={getCategoryItemStyle(syncedTemplatesSectionId)}
              onClick={() => onTagClicked(syncedTemplatesSectionId, syncedTag.name)}
            >
              <MondayLogo size={16} /> {syncedTag.name}
            </div>
          )}

          <div className={getCategoryItemStyle()} onClick={() => onTagClicked(-4, "Recommended")}>
            Recommended
          </div>

          <hr />

          {/* Bottom part - tag names from data */}
          {tags.map((tag) => (
            <div
              key={tag.id}
              className={getCategoryItemStyle(tag.id)}
              onClick={() => tag.id && onTagClicked(tag.id, tag.name)}
            >
              {tag.name}
            </div>
          ))}
        </SimpleBar>
      </div>

      {/* Main content */}
      <div className={style.mainContent}>
        {coverType ? (
          <TemplatesModalCovers
            coverType={coverType}
            onUpgradeCTAClick={onUpgradeCTAClick}
            emptyTemplatesType={selectedTag.id === consts.MY_TEMPLATES_TAG_ID ? "personal" : "account"}
            onCreateTemplate={onCreateTemplate}
            searchTerm={searchTerm}
          />
        ) : (
          <ScrollWithFade
            extraStyle={{ flex: 1, overflowY: "auto", paddingInline: "32px", display: "flex", flexDirection: "column" }}
            resetScrollKey={selectedTag.id}
          >
            {sections.length === 0 && (
              <>
                <div className={style.sectionHeader}>
                  <Skeleton style={{ maxWidth: "20ch" }} />
                </div>
                <div className={style.templateGrid} style={{ "--image-height": "125px" } as CSSProperties}>
                  <Skeleton style={{ width: "192px", height: "125px" }} />
                  <Skeleton style={{ width: "192px", height: "125px" }} />
                  <Skeleton style={{ width: "192px", height: "125px" }} />

                  <Skeleton style={{ width: "192px", height: "125px" }} />
                  <Skeleton style={{ width: "192px", height: "125px" }} />
                  <Skeleton style={{ width: "192px", height: "125px" }} />
                </div>
              </>
            )}
            {sections.map((section) => (
              <div key={section.id}>
                <div className={style.sectionHeader}>
                  <span className={style.sectionTitle}>{section.title}</span>
                  {section.showSeeAll && (
                    <button className={style.seeAll} onClick={() => onSeeAllClick(section.id, section.title)}>
                      See all
                    </button>
                  )}
                  {section.id === syncedTemplatesSectionId && (
                    <div className={style.sectionSubtitle}>
                      Create an integrated canvas and monday board—ready to use, built to sync.
                    </div>
                  )}
                </div>
                <div className={style.templateGrid} style={{ "--image-height": "125px" } as CSSProperties}>
                  {section.templates.map((template) => (
                    <TemplateCardV2
                      key={template.id}
                      template={template}
                      previewAvailable={true}
                      onClick={onTemplateClick}
                      onPreview={onPreviewTemplate}
                      onEditTemplate={onEditTemplate}
                      onDeleteTemplate={(template) => onDeleteTemplate(template.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </ScrollWithFade>
        )}
      </div>
      <CloseButton onClick={onDismiss} />
    </div>
  );
}
